import type { ChatPageContext } from './pageContext'
import type { TenantAccountViewFocus } from './tenantViewFocus'
import { tabTitleForIndex } from './tenantViewFocus'

export type ViewContextHints = {
  tenantId?: string
  tenantName?: string
  accountNumber?: string
  accountName?: string
  programId?: string
  programName?: string
  propertyId?: string
  propertyName?: string
  registrationId?: string
  registrationName?: string
  registrationPropertyName?: string
  registrationUtilityName?: string
  tariffId?: string
  tariffSchedule?: string
  tariffName?: string | null
  /** Expanded tenant-account panels from the Tenant page (live UI state). */
  openAccounts?: TenantAccountViewFocus[]
  /** When true, numeric `?tab=` maps Conductor as the last tenant-account tab. */
  includeConductor?: boolean
}

export type ViewContextDisplay = {
  /** Primary line (usually a human name) */
  headline: string
  /** Extra lines: entity type + id, tab, etc. */
  lines: string[]
  /** Full label sent to the API / system prompt */
  label: string
}

/**
 * Expand route pageContext with names from Redux / Tenant UI focus when available.
 * Does not fetch — browse stays free.
 */
export function formatViewContext(ctx: ChatPageContext, hints: ViewContextHints = {}): ViewContextDisplay {
  const entity = ctx.sharedEntity
  const lines: string[] = []
  let headline = ctx.label

  if (entity?.type === 'tenant' || entity?.type === 'tenantAccount') {
    const routeTenantId = ctx.params.tenantId
    const tenantLoaded = hints.tenantId != null && hints.tenantId === routeTenantId
    const tenantName = tenantLoaded ? hints.tenantName : undefined

    if (tenantName) {
      headline = tenantName
      lines.push(`Tenant ID · ${routeTenantId}`)
    } else if (entity.type === 'tenant') {
      headline = 'Tenant'
      lines.push(`Tenant ID · ${entity.id}`)
    }

    const openAccounts = (hints.openAccounts ?? []).filter((a) => a.open)
    const urlSan = ctx.search.serviceAccountNumber

    if (entity.type === 'tenantAccount' || urlSan) {
      const san = entity.type === 'tenantAccount' ? entity.id : urlSan
      const fromOpen = openAccounts.find((a) => a.accountNumber === san)
      const accountName =
        fromOpen?.accountName ??
        (hints.accountNumber === san ? hints.accountName : undefined)
      const tabTitle =
        fromOpen?.tabTitle ??
        (ctx.search.tab != null && ctx.search.tab !== ''
          ? tabTitleFromSearch(ctx.search.tab, hints.includeConductor === true)
          : undefined)

      const accountLine = accountName ? `Account · ${san} · ${accountName}` : `Account · ${san}`
      if (!tenantName) {
        headline = accountName ? `${san} · ${accountName}` : san ?? 'Account'
        if (routeTenantId) lines.push(`Tenant ID · ${routeTenantId}`)
      } else {
        lines.push(accountLine)
      }
      if (tabTitle) {
        lines.push(`Tab · ${tabTitle}`)
      }
    }

    // List other / all expanded panels (UI state may include several).
    if (openAccounts.length > 0) {
      const focusedSan = entity.type === 'tenantAccount' ? entity.id : urlSan
      const others = openAccounts.filter((a) => a.accountNumber !== focusedSan)
      const toList =
        entity.type === 'tenant' && !urlSan
          ? openAccounts
          : others
      if (toList.length > 0) {
        lines.push(
          entity.type === 'tenant' && !urlSan
            ? `Expanded · ${toList.map(formatOpenAccount).join('; ')}`
            : `Also expanded · ${toList.map(formatOpenAccount).join('; ')}`
        )
      }
    }
  } else if (entity?.type === 'program') {
    const programLoaded = hints.programId === entity.id
    const programName = programLoaded ? hints.programName : undefined
    headline = programName ?? 'Program'
    lines.push(`Program ID · ${entity.id}`)
    if (ctx.pathname.includes('/reconciliation')) {
      lines.push('Screen · Reconciliation')
    }
  } else if (entity?.type === 'property') {
    const propertyLoaded = hints.propertyId === entity.id
    const propertyName = propertyLoaded ? hints.propertyName : undefined
    headline = propertyName ?? 'Property'
    lines.push(`Property ID · ${entity.id}`)
    const saberScreen = ctx.label.match(/SABER \/ ([^·]+)/)?.[1]?.trim()
    if (saberScreen) {
      lines.unshift(`SABER · ${saberScreen}`)
    }
  } else if (entity?.type === 'onboarding') {
    const registrationLoaded = hints.registrationId === entity.id
    const registrationName = registrationLoaded ? hints.registrationName : undefined
    headline = registrationName ?? 'Onboarding registration'
    lines.push(`Registration ID · ${entity.id}`)
    if (registrationLoaded && hints.registrationPropertyName) {
      lines.push(`Property · ${hints.registrationPropertyName}`)
    }
    if (registrationLoaded && hints.registrationUtilityName) {
      lines.push(`Utility · ${hints.registrationUtilityName}`)
    }
  } else if (entity?.type === 'tariff') {
    const tariffLoaded = hints.tariffId === entity.id
    const schedule = tariffLoaded ? hints.tariffSchedule : undefined
    const name = tariffLoaded ? hints.tariffName : undefined
    if (schedule != null && schedule !== '') {
      headline = name != null && name !== '' ? `${schedule} · ${name}` : schedule
    } else {
      headline = 'Tariff'
    }
    lines.push(`Tariff ID · ${entity.id}`)
  } else if (entity?.type === 'user') {
    headline = 'User'
    lines.push(`User ID · ${entity.id}`)
  } else {
    headline = ctx.label
    if (ctx.pathname && ctx.pathname !== '/') {
      lines.push(ctx.pathname)
    }
  }

  const label = [headline, ...lines].filter(Boolean).join(' · ')
  return { headline, lines, label }
}

function formatOpenAccount(account: TenantAccountViewFocus): string {
  const name = account.accountName ? ` (${account.accountName})` : ''
  return `${account.accountNumber}${name} · ${account.tabTitle}`
}

function tabTitleFromSearch(tab: string, includeConductor = false): string {
  if (/^\d+$/.test(tab)) {
    return tabTitleForIndex(Number(tab), includeConductor)
  }
  return tab
}
